import { Form } from "@unform/web";
import { Building, Pencil, X } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { Zoom, toast } from "react-toastify";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { getRegistries, handleUpdatedFields } from "~/functions";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import CountryList from "country-list-with-dial-code-and-flag";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSelector } from "react-redux";
import { FullGridContext } from "../..";
import FindGeneric from "~/components/Finds/FindGeneric";
import {
  agentTypeOptions,
  agentVisionOptions,
  yesOrNoOptions,
} from "~/functions/selectPopoverOptions";

export const InputContext = createContext({});

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    name: "",
    email: "",
    user_id: null,
    loaded: false,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [agentType, setAgentType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const generalForm = useRef();
  const auth = useSelector((state) => state.auth);

  useEffect(() => {
    async function getPageData() {
      if (id !== "new") {
        try {
          const { data } = await api.get(`/agents/${id}`);
          setPageData({ ...data, loaded: true });
          const {
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          } = data;
          const registries = await getRegistries({
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          });
          setRegistry(registries);
        } catch (err) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      } else {
        setPageData({ ...pageData, loaded: true });
        setFormType("full");
      }
    }
    getPageData();
  }, []);

  async function handleGeneralFormSubmit(data) {
    if (loading) return;

    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }
    if (!data.user_id) {
      delete data.user_id;
    }
    if (id === "new") {
      setLoading(true);
      try {
        const response = await api.post(`/agents`, { ...data });
        setOpened(response.data.id);
        setPageData({ ...pageData, ...data });
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        handleOpened(null);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      } finally {
        setLoading(false);
      }
    } else if (id !== "new") {
      const updated = handleUpdatedFields(data, pageData);

      if (updated.length > 0) {
        setLoading(true);
        const objUpdated = Object.fromEntries(updated);
        try {
          await api.put(`/agents/${id}`, { ...objUpdated });
          setPageData({ ...pageData, ...objUpdated });
          setSuccessfullyUpdated(true);
          toast("Saved!", { autoClose: 1000 });
          handleOpened(null);
        } catch (err) {
          console.log(err);
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        } finally {
          setLoading(false);
        }
      }
    }
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  async function handleInactivate() {
    try {
      await api.delete(`/agents/${id}`);
      toast("Agent Inactivated!", { autoClose: 1000 });
      handleOpened(null);
    } catch (err) {
      toast(err.response.data.error, { type: "error", autoClose: 3000 });
    }
  }

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        formType === "preview" ? (
          <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
            <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <Pencil size={16} color="#fff" /> Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <X size={16} /> Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.name}</h2>
            </div>
            <div className="flex flex-1 flex-col items-left px-4 py-2 gap-1">
              <p className="border-b mb-1 pb-1">Agent Information</p>
              <div className="flex flex-row items-center gap-1 text-xs">
                <strong>Name:</strong> {pageData.name}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="general"
              >
                <Building size={16} /> General
              </RegisterFormMenu>
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <InputContext.Provider
                    value={{
                      id,
                      generalForm,
                      setSuccessfullyUpdated,
                      fullscreen,
                      setFullscreen,
                      successfullyUpdated,
                      handleCloseForm,
                      handleInactivate,
                      canceled: pageData.canceled_at,
                    }}
                  >
                    {pageData.loaded ? (
                      <>
                        <FormHeader
                          access={access}
                          title={pageData.name}
                          registry={registry}
                          InputContext={InputContext}
                          loading={loading}
                        />
                        <InputLineGroup
                          title="GENERAL"
                          activeMenu={activeMenu === "general"}
                        >
                          <FindGeneric
                            route="filials"
                            title="Filial"
                            scope="filial"
                            required
                            InputContext={InputContext}
                            defaultValue={
                              id === "new" && auth.filial.id !== 1
                                ? {
                                    id: auth.filial.id,
                                    name: auth.filial.name,
                                  }
                                : {
                                    id: pageData.filial?.id,
                                    name: pageData.filial?.name,
                                  }
                            }
                            fields={[
                              {
                                title: "Name",
                                name: "name",
                              },
                            ]}
                          />
                          <InputLine title="General Data">
                            <Input
                              type="text"
                              name="name"
                              required
                              grow
                              title="Name"
                              defaultValue={pageData.name}
                              InputContext={InputContext}
                            />
                            <SelectPopover
                              name="type"
                              required
                              grow
                              title="Type"
                              isSearchable
                              defaultValue={agentTypeOptions.find(
                                (type) => type.value === pageData.type
                              )}
                              options={agentTypeOptions}
                              InputContext={InputContext}
                              setReturnPopover={(val) => setAgentType(val[0])}
                            />
                            <SelectPopover
                              name="active"
                              required
                              grow
                              title="Is Active?"
                              isSearchable
                              defaultValue={yesOrNoOptions.find(
                                (type) => type.value === pageData.active
                              )}
                              options={yesOrNoOptions}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine>
                            <Input
                              name="old_name"
                              grow
                              title="Old Name"
                              defaultValue={pageData.old_name}
                              InputContext={InputContext}
                            />
                            <SelectPopover
                              name="vision"
                              required
                              grow
                              title="Vision"
                              isSearchable
                              defaultValue={agentVisionOptions.find(
                                (vision) => vision.value === pageData.vision
                              )}
                              options={agentVisionOptions}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <FindGeneric
                            route="partners_and_influencers"
                            title="Partner"
                            scope="partners_and_influencer"
                            type="not-agent"
                            InputContext={InputContext}
                            defaultValue={{
                              id: pageData.partners_and_influencer?.id,
                              partners_name:
                                pageData.partners_and_influencer?.partners_name,
                              social_network_type:
                                pageData.partners_and_influencer
                                  ?.social_network_type,
                              social_network:
                                pageData.partners_and_influencer
                                  ?.social_network,
                            }}
                            fields={[
                              {
                                title: "Partner's Name",
                                name: "partners_name",
                              },
                              {
                                title: "Type",
                                name: "social_network_type",
                              },
                              {
                                title: "Social Network",
                                name: "social_network",
                              },
                            ]}
                          />
                          {agentType && agentType.value === "External" && (
                            <InputLine title="Company">
                              <Input
                                type="text"
                                name="company_name"
                                required
                                grow
                                title="Company Name"
                                defaultValue={pageData.company_name}
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="company_address"
                                required
                                grow
                                title="Company Address"
                                defaultValue={pageData.company_address}
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="company_city"
                                required
                                grow
                                title="Company City"
                                defaultValue={pageData.company_city}
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="company_state"
                                required
                                grow
                                title="Company State"
                                defaultValue={pageData.company_state}
                                InputContext={InputContext}
                              />

                              <Input
                                type="text"
                                name="company_zip"
                                required
                                grow
                                title="Company Zip"
                                defaultValue={pageData.company_zip}
                                InputContext={InputContext}
                              />

                              <Input
                                type="text"
                                name="company_phone_number"
                                required
                                grow
                                title="Company Phone Number"
                                defaultValue={pageData.company_phone_number}
                                InputContext={InputContext}
                              />

                              <Input
                                type="text"
                                name="company_ein"
                                required
                                grow
                                title="Company EIN"
                                defaultValue={pageData.company_ein}
                                InputContext={InputContext}
                              />
                            </InputLine>
                          )}
                          <Input
                            type="hidden"
                            name="email"
                            defaultValue={pageData.email}
                            InputContext={InputContext}
                          />
                          <FindGeneric
                            route="users"
                            title="User"
                            scope="user"
                            setReturnFindGeneric={(user) =>
                              generalForm.current.setFieldValue("email", user?.email || "")
                            }
                            type="not-agent"
                            InputContext={InputContext}
                            defaultValue={{
                              id: pageData.user?.id,
                              name: pageData.user?.name,
                              email: pageData.user?.email,
                            }}
                            fields={[
                              {
                                title: "Name",
                                name: "name",
                              },
                              {
                                title: "E-mail",
                                name: "email",
                              },
                            ]}
                          />
                        </InputLineGroup>
                      </>
                    ) : (
                      <FormLoading />
                    )}
                  </InputContext.Provider>
                </Form>
              </div>
            </div>
          </div>
        )
      ) : null}
    </Preview>
  );
}
